import axios, {Method} from "axios";
import {useAuthStore} from "../store/authStore";
import {showMessage} from "../utils/snackbar";
import {shouldReload} from "../utils/version";
import {tokenRefresher} from "../services/firebase/tokenRefresher";
import {breadcrumb} from "../utils/sentry";

// These errors are considered warning-only and will not trigger a full error log.
const warningOnlyErrors = new Set(['ERR_NETWORK']);

async function request<T = any>(method: Method, url: string, data: any) {
    try {
        const response = await api.request<T>({method, url, data,});
        console.info(`[API] ${method} ${url}`, {requestData: data, responseData: response.data});
        return response.data;
    } catch (error: any) {
        const message =`[API] ${method} ${url} - FAILED [${error?.request?.status}] ${error?.code}`

        if (warningOnlyErrors.has(error?.code)) {
            console.warn(message, error);
        } else {
            console.error(message, error);
        }

        throw error;
    }
}


export const get = async <T = any>(url: string): Promise<T> => {
    return await request<T>("GET", url, null);
};

export const post = async <T = any>(url: string, payload: any = null): Promise<T> => {
    return await request<T>("POST", url, payload);
};

export const put = async <T = any>(url: string, payload: any): Promise<T> => {
    return await request<T>("PUT", url, payload);
};

export const patch = async <T = any>(url: string, payload: any): Promise<T> => {
    return await request<T>("PATCH", url, payload);
};

export const del = async <T = any>(url: string): Promise<T> => {
    return await request<T>("DELETE", url, null);
};

// Create an Axios instance with authentication support
const api = axios.create({
    baseURL: process.env.REACT_APP_BACKEND_URL ?? "/api",
    headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
    },
});

// Add request interceptor for authentication and FormData handling
api.interceptors.request.use(
    (config) => {
        const idToken = useAuthStore.getState().idToken;
        if (idToken) {
            config.headers.Authorization = `Bearer ${idToken}`;
        }

        if (config.data instanceof FormData) {
            config.headers["Content-Type"] = undefined;  // Let Axios set the correct Content-Type for FormData
            console.info("[API] Using FormData for request", {url: config.url, data: config.data});
        }

        return config;
    },
    async (error) => Promise.reject(error)
);

export const SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY = "show_version_update_notification";

let reloadTriggered = false;
// Add response interceptor for version checks and app refresh
api.interceptors.response.use(
    (response) => {
        if (reloadTriggered) return response;

        const serverVersion = response.headers["x-api-version"] ?? response.headers["X-Api-Version"];
        const clientVersion = process.env.REACT_APP_VERSION;

        if (shouldReload(serverVersion, clientVersion)) {
            console.info("[API] Reloading page due to version update.", {clientVersion, serverVersion});
            breadcrumb('api', 'Reloading page due to version update', {clientVersion, serverVersion})
            reloadTriggered = true;
            sessionStorage.setItem(SESSION_STORAGE_SHOW_UPDATE_NOTIFICATION_KEY, "true");
            window.location.reload();
        }
        return response;
    },
    async (error) => {
        if (error.response?.status === 401) {
            console.warn("Unauthorized request. Trying to refresh token...")
            breadcrumb('api', "Unauthorized request")
            showMessage("Ongeautoriseerd verzoek. Proberen token te vernieuwen...", "warning")

            await tokenRefresher.refresh()
        }
        return Promise.reject(error);
    }
);



